import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Create from "@/pages/Create";
import Studio from "@/pages/Studio";
import Credits from "@/pages/Credits";
import Affiliate from "@/pages/Affiliate";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Media from "@/pages/Media";
import Pricing from "@/pages/Pricing";
import Checkout from "@/pages/Checkout";
import Support from "@/pages/Support";
import Earn from "@/pages/Earn";
import Legal from "@/pages/Legal";
import CreateVideo from "@/pages/CreateVideo";
import FAQ from "@/pages/FAQ";
import Academy from "@/pages/Academy";
import Community from "@/pages/Community";
import HelpButton from "@/components/HelpButton";
import Footer from "@/components/Footer";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-xl">…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/earn" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function Shell({ children, help, footer = true }) {
  return (
    <div className="App flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      {footer && <Footer />}
      {help && <HelpButton />}
    </div>
  );
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Shell><Landing /></Shell>} />
            <Route path="/auth" element={<Shell footer={false}><Auth /></Shell>} />
            <Route path="/pricing" element={<Shell><Pricing /></Shell>} />
            <Route path="/checkout" element={<Shell><Checkout /></Shell>} />
            <Route path="/faq" element={<Shell><FAQ /></Shell>} />
            <Route path="/academy" element={<Shell><Academy /></Shell>} />
            <Route path="/communaute" element={<Shell><Community /></Shell>} />
            <Route path="/support" element={<Shell><Support /></Shell>} />
            <Route path="/legal/:slug" element={<Shell><Legal /></Shell>} />
            <Route path="/create" element={<Protected><Shell help footer={false}><Create /></Shell></Protected>} />
            <Route path="/video" element={<Protected><Shell help footer={false}><CreateVideo /></Shell></Protected>} />
            <Route path="/earn" element={<Protected><Shell help><Earn /></Shell></Protected>} />
            <Route path="/studio" element={<Protected><Shell help><Studio /></Shell></Protected>} />
            <Route path="/media" element={<Protected><Shell help><Media /></Shell></Protected>} />
            <Route path="/credits" element={<Protected><Shell help><Credits /></Shell></Protected>} />
            <Route path="/affiliate" element={<Protected><Shell help><Affiliate /></Shell></Protected>} />
            <Route path="/profile" element={<Protected><Shell help><Profile /></Shell></Protected>} />
            <Route path="/admin" element={<Protected admin><Shell footer={false}><Admin /></Shell></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
